import { CommonHelperFunctions, IKType, IKTypeField, IKuikaDataType, IKuikaDataTypeField } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

/**
 * CRUD for the project-wide, user-managed Kuika Data Types catalog.
 *
 * The catalog speaks the canonical `IKuikaDataType` shape (mirrors C# `TypeModel`) end-to-end.
 * Convert to/from `IKType` only at the designer boundary — see {@link kuikaDataTypeToIKType} and
 * {@link iKTypeToKuikaDataType}.
 */
export class ProjectDataTypesService {
  public static GetUserDataTypes(): Promise<AxiosResponse<IKuikaDataType[]>> {
    return Axios.get<null, AxiosResponse<IKuikaDataType[]>>(`${backendUrl}/domains/datatypes`);
  }

  public static CreateUserDataType(dataType: IKuikaDataType): Promise<AxiosResponse<IKuikaDataType>> {
    return Axios.post<null, AxiosResponse<IKuikaDataType>>(`${backendUrl}/domains/datatypes`, dataType);
  }

  public static UpdateUserDataType(
    originalName: string,
    dataType: IKuikaDataType
  ): Promise<AxiosResponse<IKuikaDataType>> {
    return Axios.put<null, AxiosResponse<IKuikaDataType>>(
      `${backendUrl}/domains/datatypes/${encodeURIComponent(originalName)}`,
      dataType
    );
  }

  public static DeleteUserDataType(typeName: string): Promise<AxiosResponse<void>> {
    return Axios.delete<null, AxiosResponse<void>>(`${backendUrl}/domains/datatypes/${encodeURIComponent(typeName)}`);
  }
}

/**
 * Convert a catalog type (`IKuikaDataType`) into the `IKType` shape used by `sd.types` and the
 * symbol picker. In `IKType`, a field's `type` is a nested `IKType`; here we synthesize a shallow
 * placeholder type carrying the element type NAME so name-based resolution keeps working.
 */
export function kuikaDataTypeToIKType(dataType: IKuikaDataType): IKType {
  const fields: IKTypeField[] = (dataType.elements ?? []).map((el: IKuikaDataTypeField): IKTypeField => {
    const nestedType: IKType = el.elementTypeModel
      ? kuikaDataTypeToIKType(el.elementTypeModel)
      : { id: 0, name: el.type };
    const field: IKTypeField = {
      name: el.name,
      type: nestedType,
      isList: el.isList
    };
    if (el.prefix !== undefined) {
      field.prefix = el.prefix;
    }
    return field;
  });
  return {
    id: 0,
    name: dataType.name,
    origin: dataType.origin,
    elements: fields
  };
}

/**
 * Convert an `IKType` (e.g. a datasource-derived type being imported into the catalog) into the
 * canonical `IKuikaDataType` shape. Marks the result as user-authored.
 */
export function iKTypeToKuikaDataType(type: IKType): IKuikaDataType {
  const elements: IKuikaDataTypeField[] = (type.elements ?? []).map((f: IKTypeField): IKuikaDataTypeField => {
    const hasNestedObject: boolean = !!f.type && !!f.type.elements && f.type.elements.length > 0;
    const field: IKuikaDataTypeField = {
      name: f.name,
      type: f.type ? f.type.name : "String",
      isList: f.isList
    };
    if (hasNestedObject) {
      field.elementTypeModel = iKTypeToKuikaDataType(f.type);
    }
    if (f.prefix !== undefined) {
      field.prefix = f.prefix;
    }
    return field;
  });
  return {
    name: type.name,
    origin: "user",
    elements
  };
}
